import { useMemo, useState } from 'react';
import { Add, Remove } from '@material-ui/icons';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import Paper from '@material-ui/core/Paper';
import {
  BidFileSearchResult,
  DocumentTemplateType,
  FileCategoryType,
} from '../../api-client/autogenerated';
import { Collapse } from '@material-ui/core';
import { ascendingComparator } from '../document-index/DocumentIndexUtils';
import { getUserFriendlyDocumentTemplateNamePlural } from '../../scripts/utils';
import BidFileSearchItem from './BidFileSearchItem';
import { getIconFromDocumentTemplateType } from './DocumentTypeSearchItem';

/**
 * The bid setup's files are what the Project Manual and Bid Drawings logs show, so they are
 * grouped and headed by those document types rather than as a file list of their own.
 */
const DOCUMENT_TYPE_BY_CATEGORY: Record<string, DocumentTemplateType> = {
  [FileCategoryType.BidSpecifications]: DocumentTemplateType.ProjectManual,
  [FileCategoryType.BidDrawings]: DocumentTemplateType.BidDrawings,
};

interface BidFileSearchResultsProps {
  files: BidFileSearchResult[];
  searchTerm: string;
  keywordSearchTerms: string[];
}

interface BidFileTypeGroupProps extends BidFileSearchResultsProps {
  documentType: DocumentTemplateType;
}

function BidFileTypeGroup(props: BidFileTypeGroupProps) {
  const { documentType, files, searchTerm } = props;

  const [open, setOpen] = useState(true);

  return (
    <Paper
      style={{
        marginBottom: 8,
        boxShadow: '1px 1px 10px 1px rgba(0, 0, 0, 0.1)',
      }}
    >
      <div
        onClick={() => setOpen((prev) => !prev)}
        style={{
          display: 'flex',
          position: 'relative',
          alignItems: 'center',
          padding: 16,
          borderBottom: '1px solid #DCDCDC',
          cursor: 'pointer',
          zIndex: 10,
        }}
      >
        {getIconFromDocumentTemplateType(documentType)}
        <Typography style={{ fontSize: '18px', lineHeight: '24px', fontWeight: 700 }}>
          {getUserFriendlyDocumentTemplateNamePlural(documentType)} ({files.length})
        </Typography>
        <div style={{ display: 'inline-flex', flexGrow: 100 }} />
        <IconButton style={{ padding: 0 }}>{open ? <Remove /> : <Add />}</IconButton>
      </div>
      <Collapse in={open}>
        {files
          .sort((a, b) => ascendingComparator(a, b, 'name'))
          .map((file) => (
            <BidFileSearchItem key={file.id} bidFile={file} searchTerm={searchTerm} />
          ))}
      </Collapse>
    </Paper>
  );
}

export default function BidFileSearchResults(props: BidFileSearchResultsProps) {
  const { files, searchTerm, keywordSearchTerms } = props;

  // Project Manual first, the order the bid package and the Bidding nav both use.
  const groups = useMemo(
    () =>
      [DocumentTemplateType.ProjectManual, DocumentTemplateType.BidDrawings]
        .map((documentType) => ({
          documentType,
          files: files.filter((f) => DOCUMENT_TYPE_BY_CATEGORY[f.category] === documentType),
        }))
        .filter((group) => group.files.length > 0),
    [files],
  );

  return (
    <>
      {groups.map((group) => (
        <BidFileTypeGroup
          key={group.documentType}
          documentType={group.documentType}
          files={group.files}
          searchTerm={searchTerm}
          keywordSearchTerms={keywordSearchTerms}
        />
      ))}
    </>
  );
}
